import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

// Shared "Details" popup for an Activity Tracker entry, opened via
// right-click > Details on an activity's legend row — both
// Beacon4PerformanceNetworkOverviewLegendPanel's sidebar Activities section
// and Beacon4KpiLineChart's maximize-modal legend use this same popup, so
// the layout/fields only need to be defined once. Reads straight off
// ActivityMarkerDto (see TrackerEntryDto.cs) — the "lightweight" activity
// shape this module already fetches for the markers/legend, so no extra
// API call is needed just to show it.
//
// Deliberately NOT a MUI Dialog: a Dialog is modal (backdrop blocks the rest
// of the page, focus gets trapped inside it), which is the opposite of what
// this is for — the user wants to drag it aside and keep working in/taking a
// screenshot of the module underneath (e.g. for a demo) while it stays open.
// So this is a plain draggable floating panel, portal'd straight onto
// document.body (so it always floats above everything, including the KPI
// chart's own maximize Dialog, regardless of where in the tree it was
// opened from) with no backdrop and no focus trap at all.
//
// Layout history: a CSS Grid 2-column layout was tried here and produced
// unpredictable, oversized gaps between rows ("tem muitos espaços") — CSS
// Grid's implicit row sizing didn't behave the way a simple compact list
// needs to. Reverted to a plain single-column flex list with small, fixed
// gaps — dense and predictable regardless of how many fields are empty.

// Thin, rounded scrollbar (Chrome/Edge — the corporate frontend's target
// browsers) instead of the plain OS scrollbar, which looked like it was
// "cutting through" the content when a section needed to scroll.
const SCROLLBAR_SX = {
  '&::-webkit-scrollbar':       { width: 7, height: 7 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.28)' },
}

// One label+value row. `children` is always resolved to a single string
// BEFORE being passed in (see the call sites below) — not left as raw JSX
// children — so the empty check is reliable: passing `{a} → {b}` directly as
// children used to produce an ARRAY of 3 nodes even when both a/b were
// blank, which this same `== null` check couldn't detect as "empty" (arrays
// are never null or ''), silently defeating the hide-when-empty behaviour.
function Field({ label, children, force = false }) {
  const isEmpty = children == null || children === ''
  if (isEmpty && !force) return null
  return (
    <Box sx={{ mb: 1.4 }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: 'text.primary', lineHeight: 1.4, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  )
}

// Comments get their own visually distinct block (light background "note"
// card, monospace type) rather than sitting in the same plain list as every
// other field — activity comments here are typically technical change logs
// (parameter diffs, CRQ references), which read far better in a fixed-width
// font. It scrolls INTERNALLY when long (own maxHeight), so the popup's
// outer scrollbar never has to cut through the middle of it.
function CommentsBlock({ children }) {
  const isEmpty = children == null || children === ''
  return (
    <Box sx={{ mt: 1.6 }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>
        Comments
      </Typography>
      <Box sx={{
        bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'divider',
        borderRadius: '8px', p: '10px 12px',
        maxHeight: 180, overflowY: 'auto', ...SCROLLBAR_SX,
      }}>
        <Typography sx={{
          fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.55, wordBreak: 'break-word',
          fontFamily: '"SF Mono", "Cascadia Code", Consolas, "Roboto Mono", monospace',
          color: isEmpty ? 'text.disabled' : 'text.primary',
          fontStyle: isEmpty ? 'italic' : 'normal',
        }}>
          {isEmpty ? 'No comments' : children}
        </Typography>
      </Box>
    </Box>
  )
}

// Sized to roughly hug a "typical" activity's content at default zoom — the
// user can still resize it via the corner handle. maxWidth/maxHeight are
// plain CSS caps (not user-resizable past them).
const PANEL_WIDTH  = 380
const PANEL_HEIGHT = 460

export default function Beacon4ActivityDetailsDialog({ activity, open, onClose }) {
  const [pos, setPos] = useState(null)
  const draggingRef = useRef(false)
  const dragOffsetRef = useRef({ x: 0, y: 0 })

  // Re-centers on every fresh open (including opening a DIFFERENT activity's
  // popup) rather than remembering wherever the last one was dragged to —
  // each popup starts predictably in view instead of potentially reopening
  // off-screen from an earlier drag.
  useEffect(() => {
    if (!open) return
    setPos({
      x: Math.max(16, Math.round(window.innerWidth  / 2 - PANEL_WIDTH  / 2)),
      y: Math.max(16, Math.round(window.innerHeight / 2 - PANEL_HEIGHT / 2)),
    })
  }, [open, activity?.id])

  // Escape closes it too, since there's no backdrop to click away from.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return
      setPos({ x: e.clientX - dragOffsetRef.current.x, y: e.clientY - dragOffsetRef.current.y })
    }
    const onMouseUp = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const onHeaderMouseDown = (e) => {
    draggingRef.current = true
    dragOffsetRef.current = { x: e.clientX - (pos?.x ?? 0), y: e.clientY - (pos?.y ?? 0) }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  if (!open || !activity || !pos) return null

  const title = activity.title || activity.feature || `Activity ${activity.id}`
  const owners = (activity.ownerNames ?? []).filter(Boolean).join(', ')
  const accent = activity.color ?? '#8e24aa'
  const dateRange = (activity.startDate || activity.endDate) ? `${activity.startDate ?? '?'} → ${activity.endDate ?? '?'}` : ''

  return createPortal(
    <Box sx={{
      position: 'fixed', top: pos.y, left: pos.x, zIndex: 1500,
      display: 'flex', flexDirection: 'column',
      // Native browser resize handle (bottom-right corner) — the user drags
      // it to whatever size they want, within min/max below.
      resize: 'both', overflow: 'hidden',
      width: PANEL_WIDTH, height: PANEL_HEIGHT,
      minWidth: 300, minHeight: 220,
      maxWidth: 'min(640px, 92vw)', maxHeight: 'min(80vh, 720px)',
      bgcolor: '#fff', borderRadius: '14px',
      border: '1px solid', borderColor: 'divider',
      // Coloured accent stripe down the left edge — same colour as the
      // activity's marker on the chart/legend — instead of a small swatch
      // dot buried in the header, so the "which activity is this" cue reads
      // at a glance even before looking at the title.
      borderLeft: '4px solid', borderLeftColor: accent,
      boxShadow: '0 16px 40px rgba(0,0,0,.22)',
    }}>
      {/* Drag handle — the whole header bar. The close button stops
          propagation so clicking it doesn't also start a drag. */}
      <Box
        onMouseDown={onHeaderMouseDown}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1.5, px: 2, py: 1.5, cursor: 'grab', flexShrink: 0,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: '#fafbfc',
          userSelect: 'none',
        }}
      >
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: '#1a2233', lineHeight: 1.3 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onMouseDown={e => e.stopPropagation()}
          onClick={onClose}
          sx={{ color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'text.primary', bgcolor: 'rgba(0,0,0,0.06)' } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* flex: 1 + minHeight: 0 so THIS scrolls internally as the panel is
          resized, rather than the header, and the panel never needs to grow
          taller than whatever height the user dragged it to. Plain single
          column, small fixed gaps — no CSS Grid ambiguity. */}
      <Box sx={{ p: '16px 20px 20px', flex: 1, minHeight: 0, overflowY: 'auto', ...SCROLLBAR_SX }}>
        <Field label="Date range">{dateRange}</Field>
        <Field label="Technology">{activity.technology}</Field>
        <Field label="Type">{activity.feature}</Field>
        <Field label="Side">{activity.side}</Field>
        <Field label="Vendor">{activity.vendor}</Field>
        <Field label="Team">{activity.teamName}</Field>
        <Field label="Owner(s)">{owners}</Field>
        <CommentsBlock>{activity.comments}</CommentsBlock>
      </Box>
    </Box>,
    document.body
  )
}
